import { useState, useEffect, useMemo } from 'react'
import { supabase } from '~/supabase-client'
import { useCropsContext } from '~/common/context/crops/crops-context'

/**
 * Fetches succession child crops for a set of parent crops.
 *
 * @param {Object} params
 * @param {Array} params.crops - Array of crop objects with { id }
 * @returns {Object} { successionMap: Map<parentCropId, {count, children: [{id, interval_days}]}>, loading, error }
 */
export const useMultiCropSuccessionData = ({ crops }) => {
  const [successionMap, setSuccessionMap] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { refreshKey } = useCropsContext()

  const cropIds = useMemo(
    () => (crops || []).map(c => c.id),
    [crops]
  )

  useEffect(() => {
    if (!crops || crops.length === 0) {
      setSuccessionMap(new Map())
      setLoading(false)
      setError(null)
      return
    }

    async function fetchSuccessions() {
      setLoading(true)
      setError(null)
      try {
        const { data, error: err } = await supabase
          .from('crops')
          .select('id, parent_crop_id, succession, succession_interval_days')
          .in('parent_crop_id', cropIds)
          .order('succession', { ascending: true })
        if (err) throw err
        const map = new Map()
        for (const child of (data || [])) {
          const existing = map.get(child.parent_crop_id) || { count: 0, children: [] }
          existing.count += 1
          existing.children.push({ id: child.id, interval_days: child.succession_interval_days })
          map.set(child.parent_crop_id, existing)
        }
        setSuccessionMap(map)
      } catch (err) {
        console.error('Error fetching crop successions:', err)
        setError(err.message)
        setSuccessionMap(new Map())
      } finally {
        setLoading(false)
      }
    }

    fetchSuccessions()
  }, [cropIds.join(','), refreshKey])  // eslint-disable-line react-hooks/exhaustive-deps

  return { successionMap, loading, error }
}
